import { format, parseISO } from 'date-fns'
import { useTranslate } from '@ur-solutions/react-hooks'
import { enforceHourMinuteString } from '@/modules/activities/util'
import type { DriverActivityReport } from '@/modules/activities/types.graphql'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

type UnknownVehicleSpan = NonNullable<
  DriverActivityReport['unknownVehicleGroupedActivities']
>[number]

interface UnknownVehicleActivitiesTableProps {
  spans?: readonly UnknownVehicleSpan[] | null
}

const formatDateTime = (value?: string | null) =>
  value ? format(parseISO(value), 'dd. MMM HH:mm') : '-'

export const UnknownVehicleActivitiesTable: React.FC<
  UnknownVehicleActivitiesTableProps
> = ({ spans }) => {
  const translations = useTranslate({
    title: 'activities.unknown-vehicle-activities',
    vehicle: 'common.vehicle',
    start: 'common.start',
    end: 'common.end',
    work: 'common.total-work',
    driving: 'activities.driving',
  })

  if (!spans?.length) return null

  return (
    <Card className="mt-6">
      <CardHeader className="px-6">
        <CardTitle className="text-lg font-semibold">
          {translations.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-6">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="text-left text-muted-foreground">
              <th className="px-2 py-1 font-medium">{translations.vehicle}</th>
              <th className="px-2 py-1 font-medium">{translations.start}</th>
              <th className="px-2 py-1 font-medium">{translations.end}</th>
              <th className="px-2 py-1 font-medium">{translations.work}</th>
              <th className="px-2 py-1 font-medium">{translations.driving}</th>
            </tr>
          </thead>
          <tbody>
            {spans.map((span, index) => (
              <tr
                key={`${span.regNr ?? span.vehicleVin ?? 'unknown'}-${
                  span.startTime ?? index
                }`}
                className="border-t border-border"
              >
                <td className="px-2 py-1">
                  {span.regNr || span.vehicleVin || '-'}
                </td>
                <td className="px-2 py-1">{formatDateTime(span.startTime)}</td>
                <td className="px-2 py-1">{formatDateTime(span.endTime)}</td>
                <td className="px-2 py-1">
                  {enforceHourMinuteString(span.timeWorkTotal)}
                </td>
                <td className="px-2 py-1">
                  {enforceHourMinuteString(span.timeDriving)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  )
}
